import React, { useEffect, useRef, useState } from 'react';
import { Box, Button } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { Chip, Typography } from '@mui/joy';
import { useTranslation } from 'react-i18next';
import {
    BroadcastOnHomeRounded,
    LinkOffRounded,
} from '@mui-symbols-material/w200';
import {
    AccountTreeRounded,
    AddRounded,
    ArrowForwardRounded,
    AvTimerRounded,
    BoltRounded,
    BusinessCenterRounded,
    CheckRounded,
    DescriptionRounded,
    EngineeringRounded,
    EventNoteRounded,
    HandymanRounded,
    HubRounded,
    LocationOnRounded,
    OpenInNewRounded,
    PrecisionManufacturingRounded,
    RemoveRounded,
    SensorsRounded,
    SwapHorizRounded,
    TrendingUpRounded,
    VerifiedUserRounded,
    BuildRounded,
} from '@mui-symbols-material/w300';
import {WidgetBox} from '@dreebit-registry/vsm-microservice-components';
import SignalStrengthIcon from '../../components/SignalStrengthIcon';
import AssetProductImage from '../../components/AssetProductImage';
import { getStatusChipColors, getStatusChipLabel } from '../../components/AssetNameplate';
import { connectionStatusIconFunc, connectionStatusTextFunc } from '../../components/IoTConnectionWidget/connectionStatus';
import { getSystemTypeLabel } from '../../config/systemTypes';
import { IndustryType } from '../../hooks/useIndustryType';
import { showsLiveHeroSummary } from '../../utils/heroLeadState';

/* ------------------------------------------------------------------ *
 *  Scenario types
 * ------------------------------------------------------------------ */
export type Dashboard = 'asset' | 'system';
export type Segment = 'industry' | 'semiconductor';
export type Role = 'admin' | 'user';
export type State = 'nocontract' | 'valid' | 'connected' | 'expired';
export type SystemKind = 'custom' | 'plc';
export type Variant = 'A' | 'B' | 'C' | 'D';

export interface Scenario {
    dashboard: Dashboard;
    segment: Segment;
    role: Role;
    state: State;
    systemKind: SystemKind;
    variant: Variant;
}

export const accentFor = (segment: Segment) => (segment === 'semiconductor' ? '#CC0000' : '#DB3800');
export const accentSoftFor = (segment: Segment) => (segment === 'semiconductor' ? '#FBE0E0' : '#FFE7DC');
export const appNameFor = (segment: Segment) => (segment === 'semiconductor' ? 'ARGOS' : 'OTTO');
const industryTypeFor = (segment: Segment) => (segment === 'semiconductor' ? IndustryType.SEMICONDUCTOR : IndustryType.INDUSTRY);

/* ------------------------------------------------------------------ *
 *  Sample data (hardcoded — this is a mockup)
 * ------------------------------------------------------------------ */
const STATUS_COLOR: Record<string, string> = {
    running: '#599460',
    warning: '#FFC860',
    offline: '#90ACC8',
    error: '#CC0000',
};

const EVENTS = [
    { icon: <BuildRounded />, title: 'Maintenance completed', meta: 'by M. Schröder', time: '2h ago' },
    { icon: <PrecisionManufacturingRounded />, title: 'Asset relocated to Line 3', meta: 'by System', time: 'Yesterday' },
    { icon: <VerifiedUserRounded />, title: 'Warranty updated', meta: 'by A. Klein', time: '3 days ago' },
];
const EVENTS_LONG = [
    ...EVENTS,
    { icon: <EventNoteRounded />, title: 'Commissioning sign-off', meta: 'by R. Vogel', time: '1 week ago' },
    { icon: <SensorsRounded />, title: 'Firmware updated to 3.2', meta: 'by System', time: '2 weeks ago' },
];
const FILES = [
    { icon: <DescriptionRounded />, title: 'Operating manual v4.pdf', meta: 'Product file · 2.1 MB', time: '12.05.2026' },
    { icon: <DescriptionRounded />, title: 'Service report Q1.pdf', meta: 'Asset file · 480 KB', time: '04.04.2026' },
    { icon: <DescriptionRounded />, title: 'Datasheet.pdf', meta: 'Product file · 760 KB', time: '18.02.2026' },
];
const SUBASSETS = [
    { icon: <PrecisionManufacturingRounded />, title: 'Pump module A-204', meta: 'SN 884-0021', status: 'running' },
    { icon: <PrecisionManufacturingRounded />, title: 'Controller C-11', meta: 'SN 884-0044', status: 'running' },
    { icon: <PrecisionManufacturingRounded />, title: 'Exhaust unit E-7', meta: 'SN 884-0102', status: 'warning' },
];
const REMINDERS = [
    { icon: <BuildRounded />, title: 'Oil change due', meta: 'Interval: 6 months', time: 'in 3 weeks' },
    { icon: <BuildRounded />, title: 'Filter inspection', meta: 'Interval: 12 months', time: 'in 2 months' },
];
const PRESSURE_LINES: { name: string; assets: { title: string; serial: string; connectionStatus: string }[] }[] = [
    {
        name: 'Pressure Line 1 — North',
        assets: [
            { title: 'Vacuum pump VP-12', serial: 'SN 771-3300', connectionStatus: 'RUNNING' },
            { title: 'Vacuum pump VP-13', serial: 'SN 771-3301', connectionStatus: 'RUNNING' },
            { title: 'Exhaust unit E-14', serial: 'SN 771-3320', connectionStatus: 'WARNING' },
        ],
    },
    {
        name: 'Pressure Line 2 — South',
        assets: [
            { title: 'Booster B-3', serial: 'SN 771-9001', connectionStatus: 'CURRENTLY_OFFLINE' },
            { title: 'Vacuum pump P-9', serial: 'SN 771-9100', connectionStatus: 'RUNNING' },
        ],
    },
    {
        name: 'Pressure Line 3 — Lab',
        assets: [{ title: 'Dry pump L-1', serial: 'SN 771-5500', connectionStatus: 'RUNNING' }],
    },
];
const PRESSURE_LINE_TOTAL = PRESSURE_LINES.reduce((acc, l) => acc + l.assets.length, 0);

/* Runtime / chamber sample data (mockup). */
const RUNTIME_TOTAL_H = 12480; // total runtime since installation (all assets)
const CHAMBER_AVG_H = 9200; // average runtime of all pump models in this chamber (semiconductor)
const RUNTIME_DELTA_PCT = Math.round((RUNTIME_TOTAL_H / CHAMBER_AVG_H - 1) * 100); // +36%
const CHAMBER_LABEL = 'Fab 2 · Chamber C-North';
const fmtHours = (h: number) => `${h.toLocaleString('en-US')} h`;

/* ------------------------------------------------------------------ *
 *  Scenario-derived helpers
 * ------------------------------------------------------------------ */
/**
 * Service reminders are shown only for assets without an active IoT connection.
 * Mockup rule: semiconductor assets never use IoT, industry assets only when not connected.
 */
export const showReminders = (s: Scenario) => s.segment === 'semiconductor' || s.state !== 'connected';

/**
 * Tracks whether the observed element is narrower than `threshold` CSS pixels.
 * Measures the actual content-area width (not the viewport), so it ignores the fixed
 * sidebar/chrome and naturally responds to browser zoom (zoom shrinks CSS px). Used to
 * switch Hero widgets to their compact/dense presentation on narrow or high-DPI screens.
 */
export const useCompactWidth = (threshold = 900) => {
    const ref = useRef<HTMLDivElement | null>(null);
    const [compact, setCompact] = useState(false);
    useEffect(() => {
        const el = ref.current;
        if (!el || typeof ResizeObserver === 'undefined') return;
        const ro = new ResizeObserver((entries) => {
            const w = entries[0]?.contentRect.width ?? 0;
            setCompact(w > 0 && w < threshold);
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, [threshold]);
    return { ref, compact };
};

interface EventAction {
    key: string;
    label: string;
    icon: React.ReactNode;
}

/**
 * Start-event actions available on the dashboard. The set is segment-specific (in the
 * real app it is filtered server-side per asset/location). Install only appears before
 * the asset is installed, i.e. when not yet connected.
 */
const useEventActions = (segment: Segment, state: State): EventAction[] => {
    const { t } = useTranslation();
    if (segment === 'semiconductor') {
        return [
            { key: 'remove', label: t('Remove Asset'), icon: <RemoveRounded /> },
            { key: 'exchange', label: t('Exchange'), icon: <SwapHorizRounded /> },
            { key: 'onsite', label: t('On-Site Intervention'), icon: <EngineeringRounded /> },
        ];
    }
    return [
        ...(state !== 'connected' ? [{ key: 'install', label: t('Install Asset'), icon: <AddRounded /> }] : []),
        { key: 'maintenance', label: t('Maintenance'), icon: <BuildRounded /> },
        { key: 'repair', label: t('Repair'), icon: <HandymanRounded /> },
    ];
};

/* ------------------------------------------------------------------ *
 *  Small reusable building blocks
 * ------------------------------------------------------------------ */
interface ListItem {
    icon: React.ReactNode;
    title: string;
    meta?: string;
    time?: string;
    status?: string;
}

const StatusDot = ({ status }: { status: string }) => (
    <Box sx={{ width: 10, height: 10, borderRadius: '50%', flex: 'none', backgroundColor: STATUS_COLOR[status] ?? '#bbb' }} />
);

const PreviewRow = ({ item, accent }: { item: ListItem; accent: string }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', py: '9px', borderTop: '1px solid #eee' }}>
        <Box sx={{ width: 28, height: 28, borderRadius: '6px', flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f4f4f4', color: '#666', '& svg': { fontSize: 17 } }}>
            {item.icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography level="bodyBold" sx={{ color: '#333' }} noWrap>
                {item.title}
            </Typography>
            {item.meta && (
                <Typography level="bodySmall" sx={{ color: '#777' }} noWrap>
                    {item.meta}
                </Typography>
            )}
        </Box>
        {item.status && <StatusDot status={item.status} />}
        {item.time && (
            <Typography level="bodySmall" sx={{ color: '#777', whiteSpace: 'nowrap' }}>
                {item.time}
            </Typography>
        )}
    </Box>
);

const ViewAllLink = ({ label, accent }: { label: string; accent: string }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', mt: '12px', color: accent, cursor: 'pointer' }}>
        <Typography level="bodySmallBold" sx={{ color: accent }}>
            {label}
        </Typography>
        <ArrowForwardRounded sx={{ fontSize: 16, color: accent }} />
    </Box>
);

const StatPreviewCard = ({ title, icon, count, items, viewAll, accent, dense }: { title: string; icon: React.ReactNode; count: number | string; items: ListItem[]; viewAll: string; accent: string; dense?: boolean }) => (
    <WidgetBox sx={dense ? { p: '14px' } : undefined}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Box sx={{ color: '#666', display: 'flex', '& svg': { fontSize: dense ? 19 : 22 } }}>{icon}</Box>
            <Typography level={dense ? 'titleSmall' : 'titleMediumLight'} sx={{ color: '#333' }}>
                {title}
            </Typography>
            <Typography level={dense ? 'titleLarge' : 'titleExtraLarge'} sx={{ ml: 'auto', color: '#333' }}>
                {count}
            </Typography>
        </Box>
        <Box sx={{ mt: '6px' }}>
            {(dense ? items.slice(0, 2) : items).map((it, i) => (
                <PreviewRow key={i} item={it} accent={accent} />
            ))}
        </Box>
        <ViewAllLink label={viewAll} accent={accent} />
    </WidgetBox>
);

/* IoT secondary info block — mirrors IoTConnectionWidget's NetConnectionStatus */
const NetInfoBlock = ({ title, status, underline, icon }: { title: string; status: React.ReactNode; underline?: React.ReactNode; icon?: React.ReactNode }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
        <Typography level="titleSmallLight" sx={{ color: '#666' }}>
            {title}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
                <Typography level="titleLarge">{status}</Typography>
                {underline && (
                    <Typography level="caption" sx={{ color: '#666', pl: '2px' }}>
                        {underline}
                    </Typography>
                )}
            </Box>
            {icon}
        </Box>
    </Box>
);

/* ------------------------------------------------------------------ *
 *  Slot renderers
 * ------------------------------------------------------------------ */

/** Main IoT connection panel (connected / stale). No charts — connection info only. */
const IoTConnectionPanel = ({ connectionStatus, stale, accent }: { connectionStatus: string; stale?: boolean; accent: string }) => {
    const { t } = useTranslation();
    return (
        <WidgetBox title={t('IoT Connection')} titleIcon={<BroadcastOnHomeRounded sx={{ width: 28, height: 28, color: '#333' }} />}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '13px', pt: '13px', opacity: stale ? 0.6 : 1 }}>
                {/* MAIN: connection status */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: '8px' }}>
                    <Box>
                        <Typography level="titleSmallLight" sx={{ color: '#666' }}>
                            {t('Status')}
                        </Typography>
                        <Typography level="titleExtraLarge" sx={{ pl: '8px' }}>
                            {connectionStatusTextFunc({ connectionStatus, t })}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', width: 70, height: 70, alignItems: 'center', justifyContent: 'center' }}>
                        {connectionStatusIconFunc({ connectionStatus })}
                    </Box>
                </Box>

                {/* SECONDARY: hub connectivity + network */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1, borderRight: '1px solid #ccc', py: '8px', pl: '8px', pr: '13px' }}>
                        <NetInfoBlock title={t('Connection')} status={t('Online')} underline={t('IoT Hub')} icon={<HubRounded sx={{ fontSize: 40, color: '#599460' }} />} />
                    </Box>
                    <Box sx={{ flex: 1, pl: '13px', py: '8px', pr: '8px' }}>
                        <NetInfoBlock title={t('Network')} status="LTE" underline="Vodafone" icon={<SignalStrengthIcon signalStrength={4} size={44} />} />
                    </Box>
                </Box>

                {stale && (
                    <Typography level="bodySmallItalic" sx={{ color: '#666', px: '8px' }}>
                        {t('Last-known data — contract expired. Renew to resume live updates.')}
                    </Typography>
                )}

                {/* TERTIARY: telemetry link */}
                <Box sx={{ borderTop: '1px solid #eee', pt: '12px', px: '8px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', color: accent, cursor: 'pointer' }}>
                        <OpenInNewRounded sx={{ fontSize: 18, color: accent }} />
                        <Typography level="bodyBold" sx={{ color: accent }}>
                            {t('Open telemetry & charts')}
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </WidgetBox>
    );
};

/** Passive "not connected" panel (valid contract, no device — non-admin view). */
const IoTNotConnectedPanel = () => {
    const { t } = useTranslation();
    return (
        <WidgetBox title={t('IoT Connection')} titleIcon={<BroadcastOnHomeRounded sx={{ width: 28, height: 28, color: '#333' }} />}>
            <Box sx={{ textAlign: 'center', color: '#777', py: '36px' }}>
                <LinkOffRounded sx={{ fontSize: 44, color: '#bbb' }} />
                <Typography level="titleSmall" sx={{ color: '#555', mt: '8px' }}>
                    {t('Not connected')}
                </Typography>
                <Typography level="bodySmall" sx={{ color: '#777', mt: '4px' }}>
                    {t('A device has not been connected to this contract yet.')}
                </Typography>
            </Box>
        </WidgetBox>
    );
};

/** Passive expired panel for non-admins. */
const IoTUnavailablePanel = () => {
    const { t } = useTranslation();
    return (
        <WidgetBox title={t('IoT Connection')} titleIcon={<BroadcastOnHomeRounded sx={{ width: 28, height: 28, color: '#333' }} />}>
            <Box sx={{ textAlign: 'center', color: '#777', py: '36px' }}>
                <SensorsRounded sx={{ fontSize: 44, color: '#bbb' }} />
                <Typography level="titleSmall" sx={{ color: '#555', mt: '8px' }}>
                    {t('Monitoring unavailable')}
                </Typography>
                <Typography level="bodySmall" sx={{ color: '#777', mt: '4px' }}>
                    {t('The IoT contract for this asset has expired.')}
                </Typography>
            </Box>
        </WidgetBox>
    );
};

/**
 * Promotional IoT widget — shown to ALL users for Industry without a contract.
 * Neutral styling: the app uses colour sparingly, so the accent appears only on the
 * icon and the single CTA button.
 */
const IoTPromoWidget = ({ accent, isSystem }: { accent: string; isSystem?: boolean }) => {
    const { t } = useTranslation();
    const benefits = [
        t('Live condition monitoring & telemetry'),
        t('Predictive maintenance alerts'),
        t('Reduced downtime and energy costs'),
    ];
    return (
        <WidgetBox title={t('IoT Connection')} titleIcon={<BroadcastOnHomeRounded sx={{ width: 28, height: 28, color: '#333' }} />}>
            <Box sx={{ pt: '13px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <TrendingUpRounded sx={{ fontSize: 26, color: accent }} />
                    <Typography level="titleSmall" sx={{ color: '#333' }}>
                        {t('Get more out of this ' + (isSystem ? 'system' : 'asset'))}
                    </Typography>
                </Box>
                <Typography level="body" sx={{ color: '#555', mt: '8px' }}>
                    {isSystem
                        ? t('Bring this system online to unlock live monitoring across all its pumps.')
                        : t('Connect to IoT to unlock live monitoring and smarter, predictive maintenance.')}
                </Typography>
                <Box sx={{ mt: '14px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {benefits.map((b, i) => (
                        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <CheckRounded sx={{ fontSize: 18, color: '#666' }} />
                            <Typography level="body" sx={{ color: '#333' }}>
                                {b}
                            </Typography>
                        </Box>
                    ))}
                </Box>
                <Button variant="contained" startIcon={<BoltRounded />} sx={{ mt: '18px', backgroundColor: accent }}>
                    {t('Request IoT activation')}
                </Button>
            </Box>
        </WidgetBox>
    );
};

/** Event history — the lead/primary slot for Semiconductor. */
const EventHistoryPanel = ({ accent }: { accent: string }) => {
    const { t } = useTranslation();
    return (
        <WidgetBox>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <EventNoteRounded sx={{ fontSize: 24, color: '#666' }} />
                <Typography level="titleMediumLight" sx={{ color: '#333' }}>
                    {t('Event history')}
                </Typography>
                <Typography level="titleExtraLarge" sx={{ ml: 'auto', color: '#333' }}>
                    42
                </Typography>
            </Box>
            <Box sx={{ mt: '6px' }}>
                {EVENTS_LONG.map((it, i) => (
                    <PreviewRow key={i} item={it} accent={accent} />
                ))}
            </Box>
            <ViewAllLink label={t('View full event history (42)')} accent={accent} />
        </WidgetBox>
    );
};

/** A single asset rendered as a compact "nameplate" card. */
const PressureLineAssetCard = ({ asset, segment }: { asset: { title: string; serial: string; connectionStatus: string }; segment: Segment }) => {
    const { t } = useTranslation();
    const { bg, text } = getStatusChipColors(asset.connectionStatus);
    return (
        <Box sx={{ width: 160, border: '1px solid #e0e0e0', borderRadius: '8px', p: '12px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', textAlign: 'center' }}>
            <AssetProductImage industryType={industryTypeFor(segment)} style={{ width: 56, height: 56 }} />
            <Typography level="bodyBold" sx={{ color: '#333' }} noWrap>
                {asset.title}
            </Typography>
            <Typography level="bodySmall" sx={{ color: '#777' }}>
                {asset.serial}
            </Typography>
            <Chip size="sm" sx={{ backgroundColor: bg, color: text, maxWidth: '100%' }}>
                {getStatusChipLabel(t, asset.connectionStatus)}
            </Chip>
        </Box>
    );
};

/** Pressure-lines overview — lines stacked vertically, assets laid out horizontally as nameplates. */
const PressureLinesPanel = ({ segment }: { segment: Segment }) => {
    const { t } = useTranslation();
    return (
        <WidgetBox>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <AccountTreeRounded sx={{ fontSize: 24, color: '#666' }} />
                <Typography level="titleMediumLight" sx={{ color: '#333' }}>
                    {t('Pressure lines')}
                </Typography>
                <Typography level="titleExtraLarge" sx={{ ml: 'auto', color: '#333' }}>
                    {PRESSURE_LINE_TOTAL}
                </Typography>
            </Box>
            <Box sx={{ mt: '12px', display: 'flex', flexDirection: 'column', gap: '18px' }}>
                {PRESSURE_LINES.map((line, i) => (
                    <Box key={i}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', pb: '10px', borderBottom: '1px solid #eee' }}>
                            <Typography level="titleSmall" sx={{ color: '#333' }}>
                                {line.name}
                            </Typography>
                            <Chip size="sm" variant="soft" color="neutral" sx={{ ml: 'auto' }}>
                                {line.assets.length} {t('assets')}
                            </Chip>
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '12px', pt: '12px' }}>
                            {line.assets.map((a, j) => (
                                <PressureLineAssetCard key={j} asset={a} segment={segment} />
                            ))}
                        </Box>
                    </Box>
                ))}
            </Box>
        </WidgetBox>
    );
};

/** Placeholder representing the embedded IoT-portal iframe shown for PLC system types. */
const IframeDashboardPlaceholder = ({ accent }: { accent: string }) => {
    const { t } = useTranslation();
    return (
        <WidgetBox sx={{ p: 0, overflow: 'hidden', minHeight: 520 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', px: '14px', py: '10px', backgroundColor: '#f5f5f5', borderBottom: '1px solid #ddd' }}>
                <Box sx={{ display: 'flex', gap: '5px' }}>
                    {['#ff5f57', '#febc2e', '#28c840'].map((c) => (
                        <Box key={c} sx={{ width: 11, height: 11, borderRadius: '50%', backgroundColor: c }} />
                    ))}
                </Box>
                <Typography level="bodySmall" sx={{ color: '#777', ml: '8px' }}>
                    {process.env.REACT_APP_BUSCH_IOT_URL || 'https://busch-iot-portal'}/iot-systems/…/telemetry
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 470, color: '#888', textAlign: 'center', px: '20px' }}>
                <HubRounded sx={{ fontSize: 60, color: accent, opacity: 0.7 }} />
                <Typography level="titleMedium" sx={{ color: '#555', mt: '12px' }}>
                    {t('IoT Portal dashboard (embedded iframe)')}
                </Typography>
                <Typography level="body" sx={{ color: '#777', mt: '6px', maxWidth: 420 }}>
                    {t('PLC-type systems show the Busch IoT portal dashboard directly via an authenticated iframe — the redesigned slot layout below is not used.')}
                </Typography>
                <Chip variant="soft" color="neutral" sx={{ mt: '14px' }}>
                    systemType: {getSystemTypeLabel('ECOTORQUE', t)}
                </Chip>
            </Box>
        </WidgetBox>
    );
};

/* Customer card with optional warranty badge and optional chamber location row. */
const CustomerCard = ({ segment, withWarranty, inWarranty, chamber }: { segment: Segment; withWarranty: boolean; inWarranty: boolean; chamber?: string }) => {
    const { t } = useTranslation();
    return (
        <WidgetBox title={t('Customer')} titleIcon={<BusinessCenterRounded sx={{ width: 26, height: 26, color: '#333' }} />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', mt: '12px' }}>
                <Box sx={{ width: 42, height: 42, borderRadius: '8px', backgroundColor: '#eee', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography level="titleMedium" sx={{ color: '#888' }}>
                        {segment === 'semiconductor' ? 'N' : 'M'}
                    </Typography>
                </Box>
                <Box>
                    <Typography level="titleSmall" sx={{ color: '#333' }}>
                        {segment === 'semiconductor' ? 'NanoFab Systems GmbH' : 'Meyer Industrieanlagen AG'}
                    </Typography>
                    <Typography level="bodySmall" sx={{ color: '#777' }}>
                        Hamburg, DE · Customer #4471
                    </Typography>
                </Box>
            </Box>
            {chamber && (
                <Box sx={{ mt: '14px', display: 'flex', alignItems: 'center', gap: '8px', pt: '12px', borderTop: '1px solid #eee' }}>
                    <LocationOnRounded sx={{ fontSize: 18, color: '#666' }} />
                    <Typography level="bodySmall" sx={{ color: '#555' }}>
                        {chamber}
                    </Typography>
                </Box>
            )}
            {withWarranty && (
                <Box sx={{ mt: '14px' }}>
                    {inWarranty ? (
                        <Chip color="success" variant="soft" startDecorator={<VerifiedUserRounded sx={{ fontSize: 16 }} />}>
                            {t('In warranty')} · {t('until')} 14.08.2027
                        </Chip>
                    ) : (
                        <Chip color="danger" variant="soft">
                            {t('Out of warranty')} · {t('expired')} 30.11.2025
                        </Chip>
                    )}
                </Box>
            )}
        </WidgetBox>
    );
};

/* Admin action bar — collects admin CTAs at the top. */
const AdminActionBar = ({ state, accent, accentSoft }: { state: State; accent: string; accentSoft: string }) => {
    const { t } = useTranslation();
    const cfg: Record<string, { icon: React.ReactNode; title: string; sub: string; cta: string } | null> = {
        nocontract: { icon: <AddRounded />, title: t('No IoT contract on this asset'), sub: t('Add a contract to enable IoT monitoring for this customer.'), cta: t('Add contract') },
        valid: { icon: <BoltRounded />, title: t('Contract active — device not connected'), sub: t('Connect the device to start receiving data.'), cta: t('Connect device') },
        expired: { icon: <AddRounded />, title: t('IoT contract expired'), sub: t('Extend the contract to resume monitoring.'), cta: t('Extend contract') },
        connected: null,
    };
    const c = cfg[state];
    if (!c) return null;
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', backgroundColor: accentSoft, border: `1px solid ${accent}`, borderRadius: '5px', p: '12px 16px', mb: '16px' }}>
            <Box sx={{ width: 34, height: 34, borderRadius: '50%', backgroundColor: accent, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none', '& svg': { fontSize: 20 } }}>
                {c.icon}
            </Box>
            <Box sx={{ flex: 1 }}>
                <Typography level="titleSmall" sx={{ color: '#333' }}>
                    {c.title}
                </Typography>
                <Typography level="bodySmall" sx={{ color: '#666' }}>
                    {c.sub}
                </Typography>
            </Box>
            <Button variant="contained" sx={{ backgroundColor: accent }}>
                {c.cta}
            </Button>
        </Box>
    );
};

/* ------------------------------------------------------------------ *
 *  Segmented control (used by the floating switcher)
 * ------------------------------------------------------------------ */
export function SegmentedControl<T extends string>({ label, value, options, onChange, accent, disabled }: { label: string; value: T; options: { value: T; label: string }[]; onChange: (v: T) => void; accent: string; disabled?: boolean }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px', opacity: disabled ? 0.4 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
            <Typography level="captionBold" sx={{ color: '#888', textTransform: 'uppercase', letterSpacing: '.04em' }}>
                {label}
            </Typography>
            <Box sx={{ display: 'flex', border: '1px solid #ccc', borderRadius: '6px', overflow: 'hidden' }}>
                {options.map((o, i) => {
                    const active = o.value === value;
                    return (
                        <Box
                            key={o.value}
                            onClick={() => onChange(o.value)}
                            sx={{
                                px: '12px',
                                py: '7px',
                                fontSize: '13px',
                                cursor: 'pointer',
                                whiteSpace: 'nowrap',
                                borderRight: i < options.length - 1 ? '1px solid #ccc' : 'none',
                                backgroundColor: active ? accent : '#fff',
                                color: active ? '#fff' : '#333',
                                '&:hover': { backgroundColor: active ? accent : '#f4f4f4' },
                            }}
                        >
                            {o.label}
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}

/* ------------------------------------------------------------------ *
 *  Alternative layout variants (B/C/D) — Asset dashboard only.
 *  These render a fixed sample scenario (Industry · Connected) so the
 *  refinement can compare pure layout/visual direction side-by-side.
 * ------------------------------------------------------------------ */

/** Compact KPI tile — icon + big number + label, accent-tinted edge (Variant C). */
const KpiTile = ({ icon, value, label, accent }: { icon: React.ReactNode; value: number | string; label: string; accent: string }) => (
    <Box sx={{ flex: 1, minWidth: 130, border: '1px solid #e0e0e0', borderLeft: `3px solid ${accent}`, borderRadius: '6px', p: '12px 14px', backgroundColor: '#fff' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#777', '& svg': { fontSize: 18 } }}>
            {icon}
            <Typography level="bodySmall" sx={{ color: '#777' }} noWrap>
                {label}
            </Typography>
        </Box>
        <Typography level="titleExtraLarge" sx={{ color: '#333', mt: '4px' }}>
            {value}
        </Typography>
    </Box>
);

/** Full-width labeled section header with divider + optional count chip (Variant D). */
const SectionHeader = ({ icon, label, count }: { icon: React.ReactNode; label: string; count?: number | string }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mb: '14px' }}>
        <Box sx={{ display: 'flex', color: '#555', '& svg': { fontSize: 22 } }}>{icon}</Box>
        <Typography level="titleSmall" sx={{ color: '#333', textTransform: 'uppercase', letterSpacing: '.06em' }}>
            {label}
        </Typography>
        {count != null && (
            <Chip size="sm" variant="soft" color="neutral">
                {count}
            </Chip>
        )}
        <Box sx={{ flex: 1, height: '1px', backgroundColor: '#e0e0e0', ml: '6px' }} />
    </Box>
);

const HeroKpi = ({ value, label }: { value: string; label: string }) => (
    <Box sx={{ textAlign: 'center', px: '16px' }}>
        <Typography level="titleExtraLarge" sx={{ color: '#333' }}>
            {value}
        </Typography>
        <Typography level="bodySmall" sx={{ color: '#777' }} noWrap>
            {label}
        </Typography>
    </Box>
);

/* ---- Event-start actions (segment-specific) + RMA, one presentation per variant ---- */
const RMA_KEY = 'Service request (RMA)';
const outlinedSx = { color: '#333', borderColor: '#ccc', '&:hover': { borderColor: '#999', backgroundColor: '#f7f7f7' } };

/** A — horizontal outlined button bar; RMA pushed right as a ghost external action. */
const EventActionBar = ({ segment, state, accent }: { segment: Segment; state: State; accent: string }) => {
    const { t } = useTranslation();
    const actions = useEventActions(segment, state);
    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px', mb: '16px' }}>
            {actions.map((a) => (
                <Button key={a.key} variant="outlined" startIcon={a.icon} sx={outlinedSx}>
                    {a.label}
                </Button>
            ))}
            <Button variant="text" startIcon={<OpenInNewRounded />} sx={{ ml: { sm: 'auto' }, color: accent }}>
                {t(RMA_KEY)}
            </Button>
        </Box>
    );
};

/** B — actions in the hero: all uniform outlined (no single primary), RMA a ghost external link. */
const HeroActions = ({ segment, state, accent }: { segment: Segment; state: State; accent: string }) => {
    const { t } = useTranslation();
    const actions = useEventActions(segment, state);
    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px' }}>
            {actions.map((a) => (
                <Button key={a.key} variant="outlined" startIcon={a.icon} sx={outlinedSx}>
                    {a.label}
                </Button>
            ))}
            <Button variant="text" startIcon={<OpenInNewRounded />} sx={{ color: accent }}>
                {t(RMA_KEY)}
            </Button>
        </Box>
    );
};

/** C — compact icon-only toolbar (label as hover title) + RMA external icon button. */
const EventActionToolbar = ({ segment, state, accent }: { segment: Segment; state: State; accent: string }) => {
    const { t } = useTranslation();
    const actions = useEventActions(segment, state);
    const sq = { width: 40, height: 40, border: '1px solid #ccc', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#555', backgroundColor: '#fff', '&:hover': { backgroundColor: '#f4f4f4' }, '& svg': { fontSize: 20 } };
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {actions.map((a) => (
                <Box key={a.key} title={a.label} sx={sq}>
                    {a.icon}
                </Box>
            ))}
            <Box title={t(RMA_KEY)} sx={{ ...sq, borderColor: accent, color: accent }}>
                <OpenInNewRounded />
            </Box>
        </Box>
    );
};

/** D — dedicated "Actions" section with tappable cards; RMA card visually distinct (dashed). */
const EventActionSection = ({ segment, state, accent }: { segment: Segment; state: State; accent: string }) => {
    const { t } = useTranslation();
    const actions = useEventActions(segment, state);
    const card = { minWidth: 120, flex: '1 1 120px', maxWidth: 180, border: '1px solid #e0e0e0', borderRadius: '8px', p: '14px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', cursor: 'pointer', backgroundColor: '#fff', '&:hover': { borderColor: '#bbb' }, '& svg': { fontSize: 26, color: '#555' } };
    return (
        <Box>
            <SectionHeader icon={<BoltRounded />} label={t('Actions')} />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                {actions.map((a) => (
                    <Box key={a.key} sx={card}>
                        {a.icon}
                        <Typography level="bodySmallBold" sx={{ color: '#333', textAlign: 'center' }}>
                            {a.label}
                        </Typography>
                    </Box>
                ))}
                <Box sx={{ ...card, borderColor: accent, borderStyle: 'dashed', '& svg': { fontSize: 26, color: accent } }}>
                    <OpenInNewRounded />
                    <Typography level="bodySmallBold" sx={{ color: accent, textAlign: 'center' }}>
                        {t(RMA_KEY)}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};

/* ---- Runtime widget (total since install + chamber-average comparison for semiconductor) ---- */
const RuntimeBar = ({ label, value, max, color }: { label: string; value: number; max: number; color: string }) => (
    <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: '3px' }}>
            <Typography level="bodySmall" sx={{ color: '#666' }}>
                {label}
            </Typography>
            <Typography level="bodySmallBold" sx={{ color: '#333' }}>
                {fmtHours(value)}
            </Typography>
        </Box>
        <Box sx={{ height: 8, borderRadius: 4, backgroundColor: '#eee' }}>
            <Box sx={{ height: 8, borderRadius: 4, width: `${(value / max) * 100}%`, backgroundColor: color }} />
        </Box>
    </Box>
);

const RuntimeWidget = ({ display, segment, accent }: { display: 'bar' | 'mini' | 'gauge'; segment: Segment; accent: string }) => {
    const { t } = useTranslation();
    const isSemi = segment === 'semiconductor';
    const ratio = RUNTIME_TOTAL_H / CHAMBER_AVG_H;

    if (display === 'mini') {
        return (
            <WidgetBox sx={{ p: '14px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <AvTimerRounded sx={{ fontSize: 19, color: '#666' }} />
                    <Typography level="titleSmall" sx={{ color: '#333' }}>
                        {t('Runtime')}
                    </Typography>
                    <Typography level="titleLarge" sx={{ ml: 'auto', color: '#333' }}>
                        {fmtHours(RUNTIME_TOTAL_H)}
                    </Typography>
                </Box>
                <Typography level="bodySmall" sx={{ color: '#777', mt: '2px' }}>
                    {t('Since installation')}
                </Typography>
                {isSemi && (
                    <Box sx={{ mt: '10px' }}>
                        <Box sx={{ position: 'relative', height: 8, borderRadius: 4, backgroundColor: '#eee' }}>
                            <Box sx={{ height: 8, borderRadius: 4, width: '100%', backgroundColor: accent, opacity: 0.85 }} />
                            <Box sx={{ position: 'absolute', left: `${100 / ratio}%`, top: -3, width: 2, height: 14, backgroundColor: '#666' }} />
                        </Box>
                        <Typography level="caption" sx={{ color: '#666', mt: '4px' }}>
                            +{RUNTIME_DELTA_PCT}% {t('vs chamber avg')} ({fmtHours(CHAMBER_AVG_H)})
                        </Typography>
                    </Box>
                )}
            </WidgetBox>
        );
    }

    if (display === 'gauge') {
        const pct = Math.min(ratio / 2, 1) * 100;
        return (
            <WidgetBox>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '12px' }}>
                    <AvTimerRounded sx={{ fontSize: 22, color: '#666' }} />
                    <Typography level="titleMediumLight" sx={{ color: '#333' }}>
                        {t('Runtime')}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                    <Box sx={{ width: 120, height: 120, borderRadius: '50%', flex: 'none', background: `conic-gradient(${accent} ${pct}%, #eee 0)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Box sx={{ width: 92, height: 92, borderRadius: '50%', backgroundColor: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                            <Typography level="titleLarge" sx={{ color: '#333' }}>
                                {(RUNTIME_TOTAL_H / 1000).toFixed(1)}k
                            </Typography>
                            <Typography level="caption" sx={{ color: '#777' }}>
                                {t('hours')}
                            </Typography>
                        </Box>
                    </Box>
                    <Box>
                        <Typography level="titleSmall" sx={{ color: '#333' }}>
                            {fmtHours(RUNTIME_TOTAL_H)}
                        </Typography>
                        <Typography level="bodySmall" sx={{ color: '#777' }}>
                            {t('Since installation')}
                        </Typography>
                        {isSemi && (
                            <Chip variant="soft" color="warning" sx={{ mt: '8px' }}>
                                +{RUNTIME_DELTA_PCT}% {t('vs chamber avg')}
                            </Chip>
                        )}
                    </Box>
                </Box>
            </WidgetBox>
        );
    }

    // 'bar' (Variant A) — total headline + comparison bars
    return (
        <WidgetBox>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <AvTimerRounded sx={{ fontSize: 22, color: '#666' }} />
                <Typography level="titleMediumLight" sx={{ color: '#333' }}>
                    {t('Runtime')}
                </Typography>
                <Typography level="titleExtraLarge" sx={{ ml: 'auto', color: '#333' }}>
                    {fmtHours(RUNTIME_TOTAL_H)}
                </Typography>
            </Box>
            <Typography level="bodySmall" sx={{ color: '#777' }}>
                {t('Since installation')}
            </Typography>
            {isSemi && (
                <Box sx={{ mt: '14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <RuntimeBar label={t('This asset')} value={RUNTIME_TOTAL_H} max={RUNTIME_TOTAL_H} color={accent} />
                    <RuntimeBar label={t('Chamber avg (all pumps)')} value={CHAMBER_AVG_H} max={RUNTIME_TOTAL_H} color="#bbb" />
                    <Typography level="caption" sx={{ color: '#666' }}>
                        {t('Runs')} +{RUNTIME_DELTA_PCT}% {t('vs the chamber average')}.
                    </Typography>
                </Box>
            )}
        </WidgetBox>
    );
};

/** Location context chip (chamber) — semiconductor only. */
const ChamberChip = () => (
    <Chip variant="soft" color="neutral" startDecorator={<LocationOnRounded sx={{ fontSize: 16 }} />}>
        {CHAMBER_LABEL}
    </Chip>
);

/** B — event history as a vertical timeline. Shows fewer entries in compact mode. */
const EventTimeline = ({ accent, compact }: { accent: string; compact?: boolean }) => {
    const { t } = useTranslation();
    const events = compact ? EVENTS : EVENTS_LONG;
    return (
        <WidgetBox sx={compact ? { p: '14px' } : undefined}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '14px' }}>
                <EventNoteRounded sx={{ fontSize: compact ? 19 : 22, color: '#666' }} />
                <Typography level={compact ? 'titleSmall' : 'titleMediumLight'} sx={{ color: '#333' }}>
                    {t('Event history')}
                </Typography>
                <Typography level={compact ? 'titleLarge' : 'titleExtraLarge'} sx={{ ml: 'auto', color: '#333' }}>
                    42
                </Typography>
            </Box>
            <Box sx={{ pl: '6px' }}>
                {events.map((it, i) => (
                    <Box key={i} sx={{ display: 'flex', gap: '12px' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <Box sx={{ width: 12, height: 12, borderRadius: '50%', border: `2px solid ${accent}`, backgroundColor: '#fff', flex: 'none', mt: '3px' }} />
                            {i < events.length - 1 && <Box sx={{ width: 2, flex: 1, backgroundColor: '#e0e0e0', my: '2px' }} />}
                        </Box>
                        <Box sx={{ pb: '16px', flex: 1, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Typography level="bodyBold" sx={{ color: '#333' }} noWrap>
                                    {it.title}
                                </Typography>
                                <Typography level="bodySmall" sx={{ color: '#999', ml: 'auto', whiteSpace: 'nowrap' }}>
                                    {it.time}
                                </Typography>
                            </Box>
                            <Typography level="bodySmall" sx={{ color: '#777' }}>
                                {it.meta}
                            </Typography>
                        </Box>
                    </Box>
                ))}
            </Box>
            <ViewAllLink label={t('View full event history (42)')} accent={accent} />
        </WidgetBox>
    );
};

/** D — sub-assets as a compact table. */
const SubAssetTable = ({ accent }: { accent: string }) => {
    const { t } = useTranslation();
    return (
        <WidgetBox>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '10px' }}>
                <PrecisionManufacturingRounded sx={{ fontSize: 22, color: '#666' }} />
                <Typography level="titleMediumLight" sx={{ color: '#333' }}>
                    {t('Sub-assets')}
                </Typography>
                <Typography level="titleExtraLarge" sx={{ ml: 'auto', color: '#333' }}>
                    5
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', pb: '6px', borderBottom: '1px solid #ddd' }}>
                <Typography level="captionBold" sx={{ color: '#999', flex: 1, textTransform: 'uppercase' }}>{t('Asset')}</Typography>
                <Typography level="captionBold" sx={{ color: '#999', width: 110, textTransform: 'uppercase' }}>{t('Serial')}</Typography>
                <Typography level="captionBold" sx={{ color: '#999', width: 56, textAlign: 'right', textTransform: 'uppercase' }}>{t('Status')}</Typography>
            </Box>
            {SUBASSETS.map((s, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', py: '10px', borderBottom: '1px solid #f0f0f0' }}>
                    <Typography level="bodyBold" sx={{ color: '#333', flex: 1 }} noWrap>
                        {s.title}
                    </Typography>
                    <Typography level="bodySmall" sx={{ color: '#777', width: 110 }}>
                        {s.meta?.replace('SN ', '')}
                    </Typography>
                    <Box sx={{ width: 56, display: 'flex', justifyContent: 'flex-end' }}>
                        <StatusDot status={s.status!} />
                    </Box>
                </Box>
            ))}
            <ViewAllLink label={t('View all (5)')} accent={accent} />
        </WidgetBox>
    );
};

/** Message-style hero lead (icon + title + subtitle) for passive IoT states. */
const HeroLeadMessage = ({ icon, title, sub, iconBox, headline }: { icon: React.ReactNode; title: string; sub: string; iconBox: number; headline: 'titleLarge' | 'titleExtraLarge' }) => {
    const { t } = useTranslation();
    return (
        <>
            <Box sx={{ display: 'flex', width: iconBox, height: iconBox, alignItems: 'center', justifyContent: 'center', '& svg': { fontSize: 40, color: '#bbb' } }}>
                {icon}
            </Box>
            <Box>
                <Typography level="titleSmallLight" sx={{ color: '#666' }}>
                    {t('IoT Connection')}
                </Typography>
                <Typography level={headline} sx={{ color: '#555' }}>
                    {title}
                </Typography>
                <Typography level="bodySmall" sx={{ color: '#777', mt: '4px', maxWidth: 320 }}>
                    {sub}
                </Typography>
            </Box>
        </>
    );
};

/**
 * Adaptive inline lead block for the hero banner — mirrors the Baseline status slot in a
 * compact idiom: semiconductor shows runtime (no IoT); industry drives off the IoT state.
 */
const HeroLead = ({ scenario, accent, compact, isSystem }: { scenario: Scenario; accent: string; compact?: boolean; isSystem?: boolean }) => {
    const { t } = useTranslation();
    const { segment, state, role } = scenario;
    const iconBox = compact ? 48 : 64;
    const headline: 'titleLarge' | 'titleExtraLarge' = compact ? 'titleLarge' : 'titleExtraLarge';

    // Semiconductor: no IoT — runtime lead (icon black, chamber moved to Customer widget).
    if (segment === 'semiconductor') {
        return (
            <>
                <Box sx={{ display: 'flex', width: iconBox, height: iconBox, alignItems: 'center', justifyContent: 'center', '& svg': { fontSize: compact ? 40 : 52, color: '#333' } }}>
                    <AvTimerRounded />
                </Box>
                <Box>
                    <Typography level="titleSmallLight" sx={{ color: '#666' }}>
                        {t('Runtime')}
                    </Typography>
                    <Typography level={headline} sx={{ color: '#333' }}>
                        {fmtHours(RUNTIME_TOTAL_H)}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '10px', mt: '8px' }}>
                        <Chip variant="soft" color="warning">
                            +{RUNTIME_DELTA_PCT}% {t('vs chamber avg')}
                        </Chip>
                    </Box>
                </Box>
            </>
        );
    }

    // Industry — IoT state machine (inline), driven by the production helper.
    // Device presence is implied by the scenario: connected/expired assume a live device,
    // nocontract/valid none — so the production-only "no contract but device online" lead
    // (admin live summary + disclaimer) has no scenario of its own here.
    const hasIotConnection = state === 'connected' || state === 'expired';
    if (showsLiveHeroSummary({ state, isAdmin: role === 'admin', hasIotConnection })) {
        const disclaimer =
            state === 'expired'
                ? t("Contract expired — this live data is currently visible only to you (admin). Renew the contract to restore the customer's access.")
                : state === 'nocontract'
                  ? t('No IoT contract — this live data is currently visible only to you (admin). Add a contract to make it available to the customer.')
                  : null;
        return (
            <>
                <Box sx={{ display: 'flex', width: iconBox, height: iconBox, alignItems: 'center', justifyContent: 'center' }}>
                    {connectionStatusIconFunc({ connectionStatus: 'RUNNING' })}
                </Box>
                <Box>
                    <Typography level="titleSmallLight" sx={{ color: '#666' }}>
                        {t('IoT Connection')}
                    </Typography>
                    <Typography level={headline} sx={{ color: '#333' }}>
                        {connectionStatusTextFunc({ connectionStatus: 'RUNNING', t })}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', mt: '8px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <HubRounded sx={{ fontSize: 18, color: '#599460' }} />
                            <Typography level="bodySmall" sx={{ color: '#666' }}>
                                {t('IoT Hub')} · {t('Online')}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <SignalStrengthIcon signalStrength={4} size={20} />
                            <Typography level="bodySmall" sx={{ color: '#666' }}>
                                LTE · Vodafone
                            </Typography>
                        </Box>
                    </Box>
                    {disclaimer && (
                        <Typography level="bodySmallItalic" sx={{ color: '#666', mt: '8px', maxWidth: 360 }}>
                            {disclaimer}
                        </Typography>
                    )}
                </Box>
            </>
        );
    }

    if (state === 'expired') {
        return <HeroLeadMessage icon={<SensorsRounded />} title={t('Monitoring unavailable')} sub={isSystem ? t('The IoT contract for this system has expired.') : t('The IoT contract for this asset has expired.')} iconBox={iconBox} headline={headline} />;
    }

    if (state === 'valid') {
        const sub = role === 'admin'
            ? t('The contract is active — set up the IoT connection to start receiving data.')
            : isSystem
              ? t('An administrator can set up the IoT connection for this system.')
              : t('An administrator can set up the IoT connection for this asset.');
        return <HeroLeadMessage icon={<LinkOffRounded />} title={t('Not connected')} sub={sub} iconBox={iconBox} headline={headline} />;
    }

    // nocontract — users get a passive message pointing them to an admin.
    if (role !== 'admin') {
        return (
            <HeroLeadMessage
                icon={<LinkOffRounded />}
                title={t('Not connected')}
                sub={isSystem ? t('Contact your administrator to set up an IoT contract for this system.') : t('Contact your administrator to set up an IoT contract for this asset.')}
                iconBox={iconBox}
                headline={headline}
            />
        );
    }
    // nocontract — admin promo lead (the "Add contract" CTA lives in the AdminActionBar above).
    return (
        <>
            <Box sx={{ display: 'flex', width: iconBox, height: iconBox, alignItems: 'center', justifyContent: 'center', '& svg': { fontSize: compact ? 32 : 40, color: accent } }}>
                <TrendingUpRounded />
            </Box>
            <Box>
                <Typography level="titleSmallLight" sx={{ color: '#666' }}>
                    {t('IoT Connection')}
                </Typography>
                <Typography level={headline} sx={{ color: '#333' }}>
                    {t('Not connected')}
                </Typography>
                <Typography level="bodySmall" sx={{ color: '#555', mt: '4px', maxWidth: 320 }}>
                    {isSystem
                        ? t('Bring this system online to unlock live monitoring across all its pumps.')
                        : t('Connect to IoT to unlock live monitoring and smarter, predictive maintenance.')}
                </Typography>
            </Box>
        </>
    );
};

/** Variant B — status hero banner + flowing 2-column card feed. Fully adaptive (role/state). */
const AssetVariantHero = ({ scenario, compact }: { scenario: Scenario; compact?: boolean }) => {
    const { segment, state, role } = scenario;
    const accent = accentFor(segment);
    const accentSoft = accentSoftFor(segment);
    const isIndustry = segment === 'industry';
    const isSemi = segment === 'semiconductor';
    const inWarranty = state === 'connected' || state === 'valid';
    // 'valid' drops the banner in favour of an inline Connect button (admins only).
    const showAdminBar = isIndustry && role === 'admin' && (state === 'nocontract' || state === 'expired');
    const showConnect = isIndustry && role === 'admin' && state === 'valid';
    const showTelemetry = isIndustry && state === 'connected';
    const { t } = useTranslation();
    return (
        <Box>
            {showAdminBar && <AdminActionBar state={state} accent={accent} accentSoft={accentSoft} />}
            {/* Hero banner */}
            <WidgetBox sx={{ mb: '16px' }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '24px' }}>
                    {/* Lead block — adaptive IoT status (industry) or runtime (semiconductor) */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', flex: '1 1 340px' }}>
                        <HeroLead scenario={scenario} accent={accent} compact={compact} />
                    </Box>
                    {/* KPI figures */}
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                        <HeroKpi value="5" label={t('Sub-assets')} />
                        <HeroKpi value="8" label={t('Files')} />
                        <HeroKpi value="42" label={t('Events')} />
                        {isIndustry && <HeroKpi value={fmtHours(RUNTIME_TOTAL_H)} label={t('Runtime')} />}
                        {inWarranty ? (
                            <Chip color="success" variant="soft" startDecorator={<VerifiedUserRounded sx={{ fontSize: 16 }} />}>
                                {t('In warranty')}
                            </Chip>
                        ) : (
                            <Chip color="danger" variant="soft">
                                {t('Out of warranty')}
                            </Chip>
                        )}
                    </Box>
                </Box>
                {/* Actions + telemetry */}
                <Box sx={{ borderTop: '1px solid #eee', mt: '14px', pt: '12px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px' }}>
                    {showConnect && (
                        <Button variant="contained" startIcon={<BoltRounded />} sx={{ backgroundColor: accent }}>
                            {t('Connect device')}
                        </Button>
                    )}
                    <HeroActions segment={segment} state={state} accent={accent} />
                    {showTelemetry && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', color: accent, cursor: 'pointer', ml: { md: 'auto' } }}>
                            <OpenInNewRounded sx={{ fontSize: 18, color: accent }} />
                            <Typography level="bodyBold" sx={{ color: accent }}>
                                {t('Open telemetry & charts')}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </WidgetBox>

            {/* Card feed — left: event history + reminders · right: customer + sub-assets + files */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: '16px', alignItems: 'start' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <EventTimeline accent={accent} compact={compact} />
                    {showReminders(scenario) && (
                        <StatPreviewCard dense={compact} title={t('Service reminders')} icon={<BuildRounded />} count={2} items={REMINDERS} viewAll={t('View all reminders')} accent={accent} />
                    )}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <CustomerCard segment={segment} withWarranty inWarranty={inWarranty} chamber={isSemi ? CHAMBER_LABEL : undefined} />
                    <StatPreviewCard dense={compact} title={t('Sub-assets')} icon={<PrecisionManufacturingRounded />} count={5} items={SUBASSETS} viewAll={t('View all (5)')} accent={accent} />
                    <StatPreviewCard dense={compact} title={t('Files')} icon={<DescriptionRounded />} count={8} items={FILES} viewAll={t('View all files (8)')} accent={accent} />
                </Box>
            </Box>
        </Box>
    );
};

/** Compact IoT status card used inside the dense grid (Variant C). */
const DenseIoTCard = ({ accent }: { accent: string }) => {
    const { t } = useTranslation();
    return (
        <WidgetBox sx={{ p: '14px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <BroadcastOnHomeRounded sx={{ width: 19, height: 19, color: '#333' }} />
                <Typography level="titleSmall" sx={{ color: '#333' }}>
                    {t('IoT Connection')}
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', mt: '10px' }}>
                <Box sx={{ display: 'flex', width: 44, height: 44, alignItems: 'center', justifyContent: 'center' }}>
                    {connectionStatusIconFunc({ connectionStatus: 'RUNNING' })}
                </Box>
                <Box>
                    <Typography level="titleLarge" sx={{ color: '#333' }}>
                        {connectionStatusTextFunc({ connectionStatus: 'RUNNING', t })}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <SignalStrengthIcon signalStrength={4} size={16} />
                        <Typography level="bodySmall" sx={{ color: '#666' }}>
                            LTE · Vodafone
                        </Typography>
                    </Box>
                </Box>
            </Box>
            <Box sx={{ mt: '10px', display: 'flex', alignItems: 'center', gap: '6px', color: accent, cursor: 'pointer' }}>
                <OpenInNewRounded sx={{ fontSize: 16, color: accent }} />
                <Typography level="bodySmallBold" sx={{ color: accent }}>
                    {t('Telemetry & charts')}
                </Typography>
            </Box>
        </WidgetBox>
    );
};

/** Variant C — KPI tiles row + icon toolbar + dense 3-column card grid. */
const AssetVariantDense = ({ scenario }: { scenario: Scenario }) => {
    const { segment, state } = scenario;
    const accent = accentFor(segment);
    const isIndustry = segment === 'industry';
    const isSemi = segment === 'semiconductor';
    const { t } = useTranslation();
    return (
        <Box>
            {/* KPI tiles + compact action toolbar */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px', mb: '16px' }}>
                <KpiTile icon={<PrecisionManufacturingRounded />} value={5} label={t('Sub-assets')} accent={accent} />
                <KpiTile icon={<DescriptionRounded />} value={8} label={t('Files')} accent={accent} />
                <KpiTile icon={<EventNoteRounded />} value={42} label={t('Events')} accent={accent} />
                <KpiTile icon={<AvTimerRounded />} value={fmtHours(RUNTIME_TOTAL_H)} label={t('Runtime')} accent={accent} />
                <Box sx={{ ml: { lg: 'auto' } }}>
                    <EventActionToolbar segment={segment} state={state} accent={accent} />
                </Box>
            </Box>
            {isSemi && (
                <Box sx={{ mb: '12px' }}>
                    <ChamberChip />
                </Box>
            )}
            {/* Dense grid */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', lg: '1fr 1fr 1fr' }, gap: '12px', alignItems: 'start' }}>
                {isIndustry ? (
                    <DenseIoTCard accent={accent} />
                ) : (
                    <StatPreviewCard dense title={t('Event history')} icon={<EventNoteRounded />} count={42} items={EVENTS} viewAll={t('View full event history (42)')} accent={accent} />
                )}
                <RuntimeWidget display="mini" segment={segment} accent={accent} />
                <CustomerCard segment={segment} withWarranty inWarranty />
                <StatPreviewCard dense title={t('Sub-assets')} icon={<PrecisionManufacturingRounded />} count={5} items={SUBASSETS} viewAll={t('View all (5)')} accent={accent} />
                <StatPreviewCard dense title={t('Files')} icon={<DescriptionRounded />} count={8} items={FILES} viewAll={t('View all files (8)')} accent={accent} />
                <StatPreviewCard dense title={t('Activity')} icon={<EventNoteRounded />} count={42} items={EVENTS} viewAll={t('View all events (42)')} accent={accent} />
                {showReminders(scenario) && (
                    <StatPreviewCard dense title={t('Service reminders')} icon={<BuildRounded />} count={2} items={REMINDERS} viewAll={t('View all reminders')} accent={accent} />
                )}
            </Box>
        </Box>
    );
};

/** Variant D — full-width themed sections, generous vertical rhythm. */
const AssetVariantSectioned = ({ scenario }: { scenario: Scenario }) => {
    const { segment, state } = scenario;
    const accent = accentFor(segment);
    const isIndustry = segment === 'industry';
    const { t } = useTranslation();
    const twoCol = { display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: '16px', alignItems: 'start' };
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
            <EventActionSection segment={segment} state={state} accent={accent} />
            <Box>
                <SectionHeader icon={<BroadcastOnHomeRounded />} label={t('Monitoring')} />
                {segment === 'semiconductor' && (
                    <Box sx={{ mb: '14px' }}>
                        <ChamberChip />
                    </Box>
                )}
                <Box sx={twoCol}>
                    {isIndustry ? <IoTConnectionPanel connectionStatus="RUNNING" accent={accent} /> : <EventHistoryPanel accent={accent} />}
                    <RuntimeWidget display="gauge" segment={segment} accent={accent} />
                </Box>
            </Box>
            <Box>
                <SectionHeader icon={<AccountTreeRounded />} label={t('Inventory')} count={13} />
                <Box sx={twoCol}>
                    <SubAssetTable accent={accent} />
                    <StatPreviewCard title={t('Files')} icon={<DescriptionRounded />} count={8} items={FILES} viewAll={t('View all files (8)')} accent={accent} />
                </Box>
            </Box>
            <Box>
                <SectionHeader icon={<BusinessCenterRounded />} label={t('Customer & Service')} />
                <Box sx={twoCol}>
                    <CustomerCard segment={segment} withWarranty inWarranty />
                    {showReminders(scenario) && (
                        <StatPreviewCard title={t('Service reminders')} icon={<BuildRounded />} count={2} items={REMINDERS} viewAll={t('View all reminders')} accent={accent} />
                    )}
                </Box>
            </Box>
        </Box>
    );
};

/* ------------------------------------------------------------------ *
 *  Dashboard content renderers
 * ------------------------------------------------------------------ */
const AssetDashboardPreview = ({ scenario, compact }: { scenario: Scenario; compact?: boolean }) => {
    const { segment, role, state, variant } = scenario;
    const accent = accentFor(segment);
    const accentSoft = accentSoftFor(segment);
    const { t } = useTranslation();

    // Hero (B) is fully adaptive; Dense/Sectioned (C/D) stay on the fixed Admin · Connected sample.
    if (variant === 'B') return <AssetVariantHero scenario={scenario} compact={compact} />;
    if (variant === 'C') return <AssetVariantDense scenario={scenario} />;
    if (variant === 'D') return <AssetVariantSectioned scenario={scenario} />;
    const inWarranty = state === 'connected' || state === 'valid';
    const isIndustry = segment === 'industry';
    const isSemi = segment === 'semiconductor';
    const showAdminBar = isIndustry && role === 'admin' && state !== 'connected';

    const renderStatusSlot = () => {
        if (!isIndustry) return null;
        if (state === 'nocontract') return <IoTPromoWidget accent={accent} />;
        if (state === 'valid') return <IoTNotConnectedPanel />;
        if (state === 'connected') return <IoTConnectionPanel connectionStatus="RUNNING" accent={accent} />;
        if (role === 'admin') return <IoTConnectionPanel connectionStatus="RUNNING" stale accent={accent} />;
        return <IoTUnavailablePanel />;
    };

    return (
        <Box>
            {showAdminBar && <AdminActionBar state={state} accent={accent} accentSoft={accentSoft} />}
            <EventActionBar segment={segment} state={state} accent={accent} />
            {isSemi && (
                <Box sx={{ mb: '16px' }}>
                    <ChamberChip />
                </Box>
            )}
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, lg: 7 }} container direction="column" spacing={2}>
                    {isIndustry ? (
                        <>
                            {renderStatusSlot()}
                            <StatPreviewCard title={t('Activity')} icon={<EventNoteRounded />} count={42} items={EVENTS} viewAll={t('View all events (42)')} accent={accent} />
                        </>
                    ) : (
                        <EventHistoryPanel accent={accent} />
                    )}
                    <RuntimeWidget display="bar" segment={segment} accent={accent} />
                </Grid>
                <Grid size={{ xs: 12, lg: 5 }} container direction="column" spacing={2}>
                    <CustomerCard segment={segment} withWarranty inWarranty={inWarranty} />
                    <StatPreviewCard title={t('Sub-assets')} icon={<PrecisionManufacturingRounded />} count={5} items={SUBASSETS} viewAll={t('View all (5)')} accent={accent} />
                    <StatPreviewCard title={t('Files')} icon={<DescriptionRounded />} count={8} items={FILES} viewAll={t('View all files (8)')} accent={accent} />
                    {showReminders(scenario) && (
                        <StatPreviewCard title={t('Service reminders')} icon={<BuildRounded />} count={2} items={REMINDERS} viewAll={t('View all reminders')} accent={accent} />
                    )}
                </Grid>
            </Grid>
        </Box>
    );
};

/** Variant B (systems) — hero banner + full-width pressure lines. Non-PLC only. */
const SystemVariantHero = ({ scenario, compact }: { scenario: Scenario; compact?: boolean }) => {
    const { segment, state, role } = scenario;
    const accent = accentFor(segment);
    const accentSoft = accentSoftFor(segment);
    const isIndustry = segment === 'industry';
    const showAdminBar = isIndustry && role === 'admin' && state !== 'connected';
    const showTelemetry = isIndustry && state === 'connected';
    const { t } = useTranslation();
    return (
        <Box>
            {showAdminBar && <AdminActionBar state={state} accent={accent} accentSoft={accentSoft} />}
            {/* Hero banner */}
            <WidgetBox sx={{ mb: '16px' }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '24px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', flex: '1 1 340px' }}>
                        <HeroLead scenario={scenario} accent={accent} compact={compact} isSystem />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                        <HeroKpi value={String(PRESSURE_LINE_TOTAL)} label={t('Assets')} />
                        <HeroKpi value={String(PRESSURE_LINES.length)} label={t('Pressure lines')} />
                        <HeroKpi value="42" label={t('Events')} />
                    </Box>
                </Box>
                {showTelemetry && (
                    <Box sx={{ borderTop: '1px solid #eee', mt: '14px', pt: '12px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', color: accent, cursor: 'pointer' }}>
                            <OpenInNewRounded sx={{ fontSize: 18, color: accent }} />
                            <Typography level="bodyBold" sx={{ color: accent }}>
                                {t('Open telemetry & charts')}
                            </Typography>
                        </Box>
                    </Box>
                )}
            </WidgetBox>

            {/* Full-width pressure lines (nameplate cards need horizontal room) */}
            <Box sx={{ mb: '16px' }}>
                <PressureLinesPanel segment={segment} />
            </Box>

            {/* Customer + files feed */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: '16px', alignItems: 'start' }}>
                <CustomerCard segment={segment} withWarranty={false} inWarranty={false} />
                <StatPreviewCard dense={compact} title={t('Files')} icon={<DescriptionRounded />} count={5} items={FILES} viewAll={t('View all files (5)')} accent={accent} />
                {!isIndustry && <StatPreviewCard dense={compact} title={t('Activity')} icon={<EventNoteRounded />} count={42} items={EVENTS} viewAll={t('View all events (42)')} accent={accent} />}
            </Box>
        </Box>
    );
};

const SystemDashboardPreview = ({ scenario, compact }: { scenario: Scenario; compact?: boolean }) => {
    const { segment, role, state, systemKind, variant } = scenario;
    const accent = accentFor(segment);
    const accentSoft = accentSoftFor(segment);
    const { t } = useTranslation();
    const isIndustry = segment === 'industry';

    // PLC systems always render the embedded IoT-portal iframe, regardless of layout variant.
    if (systemKind === 'plc') {
        return <IframeDashboardPlaceholder accent={accent} />;
    }
    // Non-PLC systems honour the Hero layout; other variants fall through to the baseline system layout.
    if (variant === 'B') return <SystemVariantHero scenario={scenario} compact={compact} />;

    const showAdminBar = isIndustry && role === 'admin' && state !== 'connected';

    const renderStatusSlot = () => {
        if (!isIndustry) return null;
        if (state === 'nocontract') return <IoTPromoWidget accent={accent} isSystem />;
        if (state === 'valid') return <IoTNotConnectedPanel />;
        if (state === 'connected') return <IoTConnectionPanel connectionStatus="RUNNING" accent={accent} />;
        if (role === 'admin') return <IoTConnectionPanel connectionStatus="RUNNING" stale accent={accent} />;
        return <IoTUnavailablePanel />;
    };

    return (
        <Box>
            {showAdminBar && <AdminActionBar state={state} accent={accent} accentSoft={accentSoft} />}
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, lg: 7 }} container direction="column" spacing={2}>
                    {renderStatusSlot()}
                    <PressureLinesPanel segment={segment} />
                </Grid>
                <Grid size={{ xs: 12, lg: 5 }} container direction="column" spacing={2}>
                    <CustomerCard segment={segment} withWarranty={false} inWarranty={false} />
                    {!isIndustry && <StatPreviewCard title={t('Activity')} icon={<EventNoteRounded />} count={42} items={EVENTS} viewAll={t('View all events (42)')} accent={accent} />}
                    <StatPreviewCard title={t('Files')} icon={<DescriptionRounded />} count={5} items={FILES} viewAll={t('View all files (5)')} accent={accent} />
                </Grid>
            </Grid>
        </Box>
    );
};

/** Renders the redesigned dashboard content for the given scenario (no chrome/switcher). */
export const DashboardPreviewContent = ({ scenario, compact }: { scenario: Scenario; compact?: boolean }) =>
    scenario.dashboard === 'system' ? <SystemDashboardPreview scenario={scenario} compact={compact} /> : <AssetDashboardPreview scenario={scenario} compact={compact} />;

/** The cluster of scenario toggles, used inside the floating switcher panel. */
export const ScenarioSwitcher = ({ scenario, onChange }: { scenario: Scenario; onChange: <K extends keyof Scenario>(key: K, value: Scenario[K]) => void }) => {
    const { t } = useTranslation();
    const accent = accentFor(scenario.segment);
    const isAsset = scenario.dashboard === 'asset';
    // Hero (B) and Baseline (A) are fully adaptive; Dense/Sectioned (C/D) use a fixed sample scenario.
    const axesFrozen = isAsset && (scenario.variant === 'C' || scenario.variant === 'D');
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {isAsset && (
                <SegmentedControl label={t('Layout')} value={scenario.variant} accent={accent} onChange={(v) => onChange('variant', v)} options={[{ value: 'B', label: t('Hero') }, { value: 'A', label: t('Baseline') }, { value: 'C', label: t('Dense') }, { value: 'D', label: t('Sectioned') }]} />
            )}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
                <SegmentedControl label={t('Dashboard')} value={scenario.dashboard} accent={accent} onChange={(v) => onChange('dashboard', v)} options={[{ value: 'asset', label: t('Asset') }, { value: 'system', label: t('System') }]} />
                <SegmentedControl label={t('Segment')} value={scenario.segment} accent={accent} onChange={(v) => onChange('segment', v)} options={[{ value: 'industry', label: 'Industry · OTTO' }, { value: 'semiconductor', label: 'Semiconductor · ARGOS' }]} />
                <SegmentedControl label={t('Role')} value={scenario.role} accent={accent} disabled={axesFrozen} onChange={(v) => onChange('role', v)} options={[{ value: 'admin', label: t('Admin') }, { value: 'user', label: t('User') }]} />
                <SegmentedControl label={t('State')} value={scenario.state} accent={accent} disabled={axesFrozen} onChange={(v) => onChange('state', v)} options={[{ value: 'nocontract', label: t('No contract') }, { value: 'valid', label: t('Valid · not connected') }, { value: 'connected', label: t('Connected') }, { value: 'expired', label: t('Expired') }]} />
                {scenario.dashboard === 'system' && (
                    <SegmentedControl label={t('System type')} value={scenario.systemKind} accent={accent} onChange={(v) => onChange('systemKind', v)} options={[{ value: 'custom', label: 'NON_PLC / null' }, { value: 'plc', label: t('PLC (iframe)') }]} />
                )}
            </Box>
            {axesFrozen && (
                <Typography level="bodySmall" sx={{ color: '#999' }}>
                    {t('Dense & Sectioned previews use a fixed sample scenario (Admin · Connected) — toggle Segment to compare.')}
                </Typography>
            )}
        </Box>
    );
};

export default DashboardPreviewContent;
